import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import { DataEntry } from '../bk-web-app/transaction-manager/types';
import formattedCentsAmount from './formattedCentsAmount';
import { differenceInDays } from 'date-fns';
import { gql, useMutation } from '@apollo/client';
import { BULK_EDIT_DATA_ENTRIES } from './queries';
import { useClient } from '../bk-web-app/ClientContext';
import confetti from 'canvas-confetti';
import { CREATE_JOURNAL_ENTRY_INTERNAL_NOTE } from '../bk-web-app/message-list/queries';

interface PotentialMatchesTooltipProps {
  transactions: (DataEntry & { accountName: string })[];
  parentTransaction: { id: string; accountName: string | null; transactionDate: string };
  moneyInTransitLedgerId?: string;
  onActionRef?: React.MutableRefObject<((action: 'match' | 'skip') => Promise<void>) | null>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const TooltipContainer = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.silt};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  padding: 8px;
  position: fixed;
  z-index: 5000;
  width: 60vw;
  min-width: 600px;
  max-width: 900px;
  max-height: 50vh;
  overflow-y: auto;
  transform: translate(-30px, 10px);
  border-radius: 4px;
  cursor: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
`;

const Header = styled.div`
  font-size: 16px;
  background: #f0f8f8;
  margin: -8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 38px;
  flex-direction: column;
  justify-content: center;
`;

const TableHeader = styled.th`
  border-bottom: 1px solid ${colors.silt};
  padding: 4px;
  text-align: left;
  font-weight: 600;
`;

const TableRow = styled.tr<{ isHighlighted: boolean }>`
  &:nth-child(even) {
    background-color: ${colors.shell};
  }
  &:hover {
    background-color: ${colors.brook};
  }
  background-color: ${props => (props.isHighlighted ? '#cbfafa' : '')} !important;
`;

const TableCell = styled.td`
  padding: 4px;
`;

const LedgerHeader = styled.h3`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 0px;
`;

const ColoredTableCell = styled(TableCell)<{ isDebit: boolean }>`
  color: ${props => (props.isDebit ? '#217950' : '#ae280e')};
  font-weight: 500;
  white-space: nowrap;
`;

const DateTableCell = styled(TableCell)<{ isInRange: boolean }>`
  color: ${props => (props.isInRange ? 'green' : 'inherit')};
  font-weight: ${props => (props.isInRange ? '600' : '400')};
  white-space: nowrap;
`;

const InfoMessage = styled.div`
  background-color: #e1f5fe;
  border: 1px solid #0288d1;
  border-radius: 4px;
  color: #0277bd;
  margin: 8px 0;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
`;

const ConfirmationBtn = styled.button<{ disabled?: boolean }>`
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => (props.disabled ? '#ccc' : 'linear-gradient(135deg, #4a90e2, #007aff)')};
  border-radius: 12px;
  text-align: center;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  border: none;
  transition: transform 0.1s ease-in-out;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  &:focus {
    outline: 2px solid #4a90e2;
    outline-offset: 2px;
  }
`;

/**
 * DuplicateTransactionsTooltip - Displays potential duplicate transactions for matching
 */
const PotentialMatchesTooltip: React.FC<PotentialMatchesTooltipProps> = ({
  transactions,
  parentTransaction,
  moneyInTransitLedgerId,
  onActionRef,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [loadingTransactionId, setLoadingTransactionId] = useState<string | null>(null);
  const [journalEntryIdToSkip, setJournalEntryIdToSkip] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [bulkEditDataEntries] = useMutation(BULK_EDIT_DATA_ENTRIES);
  const [createJournalEntryInternalNote] = useMutation(CREATE_JOURNAL_ENTRY_INTERNAL_NOTE);
  const client = useClient();

  // Group transactions by ledger account name for organization
  const groupedTransactions = useMemo(() => {
    return transactions.reduce((acc: Record<string, DataEntry[]>, transaction) => {
      const ledgerName = transaction.ledgerAccount?.name;
      (acc[ledgerName] ??= []).push(transaction);
      return acc;
    }, {} as Record<string, DataEntry[]>);
  }, [transactions]);

  // Stop propagation of mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleMatch = useCallback(
    async (matchedTransactionId: string) => {
      setLoadingTransactionId(matchedTransactionId);

      const dataEntryIds = [parentTransaction.id, matchedTransactionId];
      const updateDataEntriesInput = {
        clientId: client.id,
        dataEntryIds,
        ledgerAccountId: moneyInTransitLedgerId,
      };

      try {
        await bulkEditDataEntries({
          variables: { updateDataEntriesInput },
          update: (cache, { data: { updateDataEntries } }) => {
            updateDataEntries.dataEntries.forEach(dataEntry => {
              cache.writeFragment({
                id: `DataEntry:${dataEntry.id}`,
                fragment: gql`
                  fragment bulkEditDataEntryLedgerAccount on DataEntry {
                    ledgerAccount {
                      id
                      __typename
                    }
                    __typename
                  }
                `,
                data: {
                  ledgerAccount: {
                    id: moneyInTransitLedgerId,
                    __typename: 'LedgerAccount',
                  },
                  __typename: 'DataEntry',
                },
              });
            });
          },
        });
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
      } catch (error) {
        console.error('Error matching transactions:', error);
      } finally {
        setLoadingTransactionId(null);
      }
    },
    [parentTransaction.id, client.id, moneyInTransitLedgerId, bulkEditDataEntries],
  );

  const handleSkip = useCallback(
    async (idToSkip = '') => {
      if (!journalEntryIdToSkip && !idToSkip) return;

      try {
        await createJournalEntryInternalNote({
          variables: {
            body: 'Skipped as a MITO consideration',
            parentId: idToSkip ?? journalEntryIdToSkip,
          },
        });
      } catch (error) {
        console.error('Error creating journal entry internal note:', error);
      } finally {
        setJournalEntryIdToSkip(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [journalEntryIdToSkip],
  );

  const handleAutoAction = React.useCallback(
    async (action: 'match' | 'skip') => {
      if (transactions.length === 1) {
        if (action === 'match') {
          await handleMatch(transactions[0].id);
        } else if (action === 'skip') {
          await handleSkip(transactions[0].journalEntry.id);
        }
      } else {
        // Show info message for multiple matches
        setInfoMessage(
          `Found ${transactions.length} potential matches. Please review and select manually.`,
        );
        setTimeout(() => setInfoMessage(null), 3000); // Clear message after 3 seconds
      }
    },
    [transactions, handleMatch, handleSkip],
  );

  // Expose the auto-action function to parent component
  React.useEffect(() => {
    if (onActionRef) {
      onActionRef.current = handleAutoAction;
    }
  }, [handleAutoAction, onActionRef]);

  const openSkipConfirmation = (journalEntryId: string) => {
    setJournalEntryIdToSkip(journalEntryId);
  };

  const closeSkipConfirmation = () => {
    setJournalEntryIdToSkip(null);
  };

  return (
    <TooltipContainer
      onMouseDown={handleMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="dialog"
    >
      <Header>
        Potential Transaction Matches
        <span style={{ fontSize: '12px' }}>({parentTransaction.accountName})</span>
      </Header>
      {infoMessage && <InfoMessage>{infoMessage}</InfoMessage>}
      {journalEntryIdToSkip && (
        <div>
          <h2>Warning!</h2>
          <p>Skip the transaction only if you&apos;re certain that:</p>
          <ul style={{ marginLeft: '20px', listStyleType: 'disc' }}>
            <li>It does not match any other transactions, not just the one currently displayed.</li>
            <li>It does not match any other transactions now or in the future.</li>
          </ul>
          <p>
            Keep in mind: transactions with terms like &quot;transfer&quot;, &quot;xfer&quot;, or
            &quot;payable&quot; are often good candidates for matching to others, and should not be
            skipped from the tool.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <ConfirmationBtn
              onClick={() => handleSkip(journalEntryIdToSkip ?? '')}
              style={{ background: '#e0e0e0', color: '#333' }}
            >
              Confirm Skip
            </ConfirmationBtn>
            <ConfirmationBtn onClick={closeSkipConfirmation}>Cancel</ConfirmationBtn>
          </div>
        </div>
      )}
      {Object.entries(groupedTransactions).map(([ledgerName, ledgerTransactions]) => (
        <div
          key={ledgerName}
          style={{
            pointerEvents: journalEntryIdToSkip ? 'none' : 'auto',
            opacity: journalEntryIdToSkip ? 0.5 : 1,
          }}
        >
          <LedgerHeader>{ledgerName}</LedgerHeader>
          <Table>
            <thead>
              <tr>
                <TableHeader></TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Amount</TableHeader>
                <TableHeader>Account</TableHeader>
              </tr>
            </thead>
            <tbody>
              {ledgerTransactions.map(transaction => (
                <TableRow
                  key={transaction.id}
                  isHighlighted={journalEntryIdToSkip === transaction.journalEntry.id}
                >
                  <TableCell style={{ width: '150px', display: 'flex', gap: '8px' }}>
                    <ConfirmationBtn
                      onClick={() => handleMatch(transaction.id)}
                      disabled={loadingTransactionId !== null || !moneyInTransitLedgerId}
                      aria-label="Match this transaction"
                      title={
                        moneyInTransitLedgerId
                          ? 'Match this transaction'
                          : 'Money in Transit ledger not set'
                      }
                    >
                      {loadingTransactionId === transaction.id ? 'Processing...' : 'Match'}
                    </ConfirmationBtn>
                    <ConfirmationBtn
                      onClick={() => openSkipConfirmation(transaction.journalEntry.id)}
                      disabled={loadingTransactionId !== null}
                      aria-label="Skip this transaction"
                      title="Skip this transaction"
                      style={{ background: '#e0e0e0', color: '#333' }}
                    >
                      {journalEntryIdToSkip ? 'Skipping...' : 'Skip'}
                    </ConfirmationBtn>
                  </TableCell>
                  <DateTableCell
                    isInRange={
                      differenceInDays(
                        parentTransaction.transactionDate,
                        transaction.journalEntry.dateOfTransaction,
                      ) <= 5
                    }
                  >
                    {transaction.journalEntry.dateOfTransaction}
                  </DateTableCell>
                  <TableCell>{transaction.journalEntry.vendorName}</TableCell>
                  <ColoredTableCell isDebit={transaction.amountInCents >= 0}>
                    {formattedCentsAmount(Math.abs(transaction.amountInCents))}
                  </ColoredTableCell>
                  <TableCell>{transaction.accountName}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </div>
      ))}
    </TooltipContainer>
  );
};

export default PotentialMatchesTooltip;
